/*
Search Dropdown (js)

### Html Attributes

```
data-search-dropdown
data-search-dropdown--url="/api/episerver/Search/GetAutoCompleteAndDidYouMean"
data-search-dropdown--min-query-length="3"
data-search-dropdown--ignore-key-codes="9 16 17 18 33 34 35 36 37 38 39 40 44 91 92 93 224"
data-search-dropdown--escape-key-codes="27"
data-search-dropdown--search-input-selector="(here) [data-search-dropdown__input-field]"
data-search-dropdown--search-cancel-selector="(here) [data-search-dropdown__cancel-button]"
data-search-dropdown--search-submit-selector="(here) [data-search-dropdown__submit-button]"
data-search-dropdown--dropdown-template-selector="(here) [data-search-dropdown__result-container]"
data-search-dropdown--results-container-selector="(here) [data-search-dropdown__result-container]"
data-search-dropdown--typing-debounce-ms="500"
data-search-dropdown--typing-debounce-edge="trailing | leading"
data-search-dropdown--open-class="search-dropdown--is-open"
data-search-dropdown--sending-class="search-dropdown--is-sending"
data-search-dropdown--error-class="search-dropdown--error"
data-search-dropdown--placement="bottom"
data-search-dropdown--placement-target="(self)"
```
*/

import $ from 'jquery';
import debug from "debug";

import debounce from '../../lib/debounce';
import {mustache} from '../../lib/strings';
import {selectTarget} from '../../lib/selectors';
import {isType} from '../../lib/types';

import { ELEMENT_SELECTOR, PLUGIN_NAME } from './index';

const log = debug(`core/modules/${PLUGIN_NAME}`);

export class SearchDropdown {

  constructor(element, options) {
    log('construct', element, options);

    this.options = options;
    this.element = element;
    this.$element = $(element);

    this.options.typingDebounceMs = parseInt(this.options.typingDebounceMs);

    this.options.escapeKeyCodes = this.options.escapeKeyCodes.split(' ');
    this.options.ignoreKeyCodes = this.options.ignoreKeyCodes.split(' ');
    this.options.enterKeyCodes = this.options.enterKeyCodes.split(' ');

    this.hasFocus = false;
    this.focsusedElement = null;
    this.searchCancelled = false;

    this.searchField = selectTarget(this.options.searchInputSelector, this.$element);
    this.submitButton = selectTarget(this.options.searchSubmitSelector, this.$element);
    this.cancelButton = selectTarget(this.options.searchCancelSelector, this.$element);
    this.resultsContainer = selectTarget(this.options.resultsContainerSelector, this.$element);
    this.formElement = selectTarget(this.options.formSelector, this.$element);

    this.templateSource = selectTarget(this.options.dropdownTemplateSelector, this.$element);
    this.template = this.templateSource.text();
    this.templateSource.data('original-content', this.template);
    this.templateSource.empty();

    this.watch();


    log('ready', this);

  }

  watch () {

    const debounceImmediate = this.options.typingDebounceEdge === 'leading';
    this.searchField.on('keyup', debounce(this.send.bind(this),
                                                  this.options.typingDebounceMs,
                                                  debounceImmediate));

    $(document).on('click', (event) => {
      const hasTarget = this.$element.has(event.target);
      if (hasTarget && hasTarget.length) {
        this.open();
      } else {
        this.close();
      }
    });

    this.formElement.on('submit', () => {
    	this.formElement.addClass('submitting');
    });

    this.cancelButton.on('click', () => {
      log('cancelClick');
      this.cancel();
      this.reset();
    });

    log('watching');

  }

  send (event) {
    this.close();
    const query = this.searchField.val();
    const {keyCode} = event;
    const {
      ignoreKeyCodes,
      escapeKeyCodes,
      enterKeyCodes,
      minQueryLength,
      errorClass,
      sendingClass,
      disableSuggest,
      openClass,
      url
    } = this.options;

    log('send', query);
    if (disableSuggest =="1") {
	      return;
    }
    else if (query && query.length < minQueryLength) {
      this.$element.trigger(`${PLUGIN_NAME}.notMinLength`);

      log('minQueryLength', query.length);
      return;
    }

    else if (ignoreKeyCodes.includes(keyCode)) {
      this.$element.trigger(`${PLUGIN_NAME}.keyIgnored`, {keyCode});

      log(`key[${keyCode}] ignored since it's in options.ignoreKeyCodes[${ignoreKeyCodes}]`);
      return;
    }

    else if (escapeKeyCodes.includes(keyCode) ) {
      this.$element.trigger(`${PLUGIN_NAME}.escape`);
      log(`key[${keyCode}] triggered close since it's in options.escapeKeyCodes[${escapeKeyCodes}]`);

      this.close();
      return;
    }

    else if (enterKeyCodes.includes(keyCode)) {
    	this.cancel();
    }

    else if (query && query.length) {
      // this.submitButton.attr('disabled', true);
      this.$element.trigger(`${PLUGIN_NAME}.beforeSend`);
      this.$element.addClass(sendingClass);
      this.$element.removeClass(errorClass);

      $.ajax({
        url,
        data: {
          q: query
        },
        success: (data, status, request) => {
          log('success', data, status);
          var formSubmitting = this.formElement.hasClass('submitting');
          if (!formSubmitting) {
	          this.submitButton.removeAttr('disabled');
	          this.$element.removeClass(sendingClass);
	          this.$element.removeClass(errorClass);
	          this.render(data);
	          this.$element.addClass(openClass);
	          this.$element.trigger(`${PLUGIN_NAME}.rendered`);
      	  }

        },
        error: (data, status, request) => {
          log('error', data, status);

          this.submitButton.removeAttr('disabled');
          this.$element.removeClass(openClass);
          this.$element.removeClass(sendingClass);
          this.$element.addClass(errorClass);
          this.$element.trigger(`${PLUGIN_NAME}.error`, {data, status, request});

        }
      });
    }
    else {
      this.cancel();
    }

    this.$element.trigger(`${PLUGIN_NAME}.afterSend`);
  }

  onFocusChanged (event) {
    log('onFocusChanged', event);
    this.focusedElement = event.toElement;
    const hasFocus = this.$element.has(this.focusedElement);
    this.hasFocus = hasFocus && hasFocus.length > 0;

    logger.log('onFocusIn.hasFocus', this.hasFocus);

    if (!this.hasFocus) {
      debounce(this.close.bind(this), 50);
    }
  }

  render (data) {
    log('render', data, this.template);
    const context = Object.keys(data).reduce((result, key) => {
        const value = data[key];
        result[key] = value;
        if (value === 0) {
        	result[`${key}IsEmpty`] = true;
        } else if (isType(value, 'array')) {
          result[`${key}IsEmpty`] = value.length === 0;
        } else if(isType(value, 'string')) {
          result[`${key}IsEmpty`] = value.length === 0 ;
        } else if(isType(value, 'undefined')) {
          result[`${key}IsEmpty`] = true;
        } else if(isType(value, 'number')) {
          result[`${key}IsEmpty`] = value == 0;
        } else if(isType(value, 'object')) {
          result[`${key}IsEmpty`] = Object.keys(value).length == 0;
        }
        return result;
      }, {});
    const html = mustache(this.template, context);
    log('rendered', html);
    this.resultsContainer.html(html);
  }

  reset (event) {
    log('reset');
    this.submitButton.removeAttr('disabled');
    this.searchField.val('');
    this.searchField.EmitOnDirty && this.searchField.EmitOnDirty('clean');
    this.resultsContainer.empty();
  }

  open () {
    log('open');
    if (this.options.disableSuggest !="1") {
	    this.$element.addClass(this.options.openClass);
	    this.$element.trigger(`${PLUGIN_NAME}.open`);
    }
  }

  close() {
    log('close');
    this.$element.removeClass(this.options.hasErrorClass);
    this.$element.removeClass(this.options.sendingClass);
    this.$element.removeClass(this.options.openClass);
    this.submitButton.removeAttr('disabled');
    this.$element.trigger(`${PLUGIN_NAME}.closed`);

  }

  cancel() {
    log('cancel');
    this.searchCancelled = true;
    this.reset();
    this.close();
  }

}

SearchDropdown.DEFAULTS = {
  /*
  http://keycode.info/
    9 = tab
    16 = shift
    17 = ctrl
    18 = alt
    37 = left
    38 = up
    39 = right
    40 = down
    32 = space
    91 = left super
    92 = right super
    //https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/keyCode#Constants_for_keyCode_value
  */
  minQueryLength: 3,
  ignoreKeyCodes: "9 16 17 18 33 34 35 36 37 38 39 40 44 91 92 93 224",
  escapeKeyCodes: "27",
  enterKeyCodes: "13",
  searchInputSelector: '(here) [data-search-dropdown__input-field]',
  searchCancelSelector: '(here) [data-search-dropdown__cancel-button]',
  searchSubmitSelector: '(here) [data-search-dropdown__submit-button]',
  dropdownTemplateSelector: '(here) [data-search-dropdown__result-container]',
  resultsContainerSelector: '(here) [data-search-dropdown__result-container]',
  formSelector: '(here) form',
  typingDebounceMs: 500,
  typingDebounceEdge: 'trailing',
  openClass: 'search-dropdown--is-open',
  sendingClass: 'search-dropdown--is-sending',
  errorClass: 'search-dropdown--error',
  placement: 'bottom',
  disableSuggest : false,
  placementTarget: '(self)'
};





// WEBPACK FOOTER //
// ./project/client/src/core/modules/search-dropdown/plugin.js